import { OrderModel as OrderApi } from '@/http/models/Order/Order'
import { toast } from 'sonner'

import { useState } from 'react'

import { OrderHttpParams } from '@/types/http-params/OrderHttpParams'
import { OrderModel } from '@/types/model/Order'

export const useOrderMutations = () => {
  const [loading, setLoading] = useState(false)

  const handleRequest = async <T>(
    requestFn: () => Promise<any>,
    successMessage?: string
  ): Promise<T | null> => {
    setLoading(true)
    try {
      const response = await requestFn()
      if (response.success) {
        if (successMessage) toast.success(successMessage)
        return response.data.result
      } else {
        toast.error(response.error?.data?.message || 'Operation failed')
        return null
      }
    } catch (error: any) {
      toast.error(error.message || 'An unexpected error occurred')
      return null
    } finally {
      setLoading(false)
    }
  }

  const createOrder = (body: OrderHttpParams.Create['body']) =>
    handleRequest<OrderModel.Order>(() => OrderApi.create({ body }), 'Order created successfully')

  const updateStatus = (id: string, status: string) =>
    handleRequest<OrderModel.Order>(
      () => OrderApi.updateStatus({ params: { id }, body: { status } }),
      'Order status updated'
    )

  const updateDiscount = (id: string, discount: number) =>
    handleRequest<OrderModel.Order>(
      () => OrderApi.updateDiscount({ params: { id }, body: { discount } }),
      'Order discount updated'
    )

  const updateCustomerInfo = (id: string, body: OrderHttpParams.UpdateCustomerInfo['body']) =>
    handleRequest<OrderModel.Order>(
      () => OrderApi.updateCustomerInfo({ params: { id }, body }),
      'Customer info updated'
    )

  const updateAddress = (id: string, address_id: string) =>
    handleRequest<OrderModel.Order>(
      () => OrderApi.updateAddress({ params: { id }, body: { address_id } }),
      'Order address updated'
    )

  const addItem = (id: string, body: OrderHttpParams.AddItem['body']) =>
    handleRequest<OrderModel.Order>(
      () => OrderApi.addItem({ params: { id }, body }),
      'Item added to order'
    )

  const updateItemDiscount = (id: string, itemId: string, discount: number) =>
    handleRequest<OrderModel.Order>(
      () => OrderApi.updateItemDiscount({ params: { id, itemId }, body: { discount } }),
      'Item discount updated'
    )

  const updateItemQuantity = (id: string, itemId: string, quantity: number) =>
    handleRequest<OrderModel.Order>(
      () => OrderApi.updateItemQuantity({ params: { id, itemId }, body: { quantity } }),
      'Item quantity updated'
    )

  const removeItem = (id: string, itemId: string) =>
    handleRequest<OrderModel.Order>(
      () => OrderApi.removeItem({ params: { id, itemId } }),
      'Item removed from order'
    )

  const addCoupon = (id: string, coupon_code: string) =>
    handleRequest<OrderModel.Order>(
      () => OrderApi.addCoupon({ params: { id }, body: { coupon_code } }),
      'Coupon applied'
    )

  const removeCoupon = (id: string) =>
    handleRequest<OrderModel.Order>(
      () => OrderApi.removeCoupon({ params: { id } }),
      'Coupon removed'
    )

  return {
    loading,
    createOrder,
    updateStatus,
    updateDiscount,
    updateCustomerInfo,
    updateAddress,
    addItem,
    updateItemDiscount,
    updateItemQuantity,
    removeItem,
    addCoupon,
    removeCoupon,
  }
}
